import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  HostListener,
  Input,
  OnDestroy,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppApis } from '@app/core/APIs/app-apis';
import { RoleService } from '@app/shared/services/roles.service';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { HearingEnrollmentStateService } from './hearing-enrollment-state.service';

interface Definition {
  definitionVersionId: number;
  sourceCode: string;
  scopeTypeCode: string;
  scopeKey: string;
  hearingTypeCode: string;
}
interface Setup {
  id: number;
  revision: number;
  eTag: string;
  status: string;
  definition: Definition;
  workflowCode: string;
  entryStepCode: string;
}
interface Source {
  code: string;
  label: string;
  scopeTypeCode: string;
  scopeKey: string;
  hearingTypeCode: string;
  installed: boolean;
}
interface Publication {
  id: number;
  workflowCode: string;
  definitionVersionCode: string;
  displayName: string;
  scopeTypeCode: string;
  scopeKey: string;
}
interface Catalog {
  versions: Setup[];
  sources: Source[];
  publications: Publication[];
  configurations: { id: number; label: string }[];
}
interface Report {
  publicationReady: boolean;
  connectionReady: boolean;
  manualReady: boolean;
  recurringReady: boolean;
  checks: {
    code: string;
    ready: boolean;
    label: string;
    message: string;
    owner: string;
    actionUrl: string | null;
  }[];
}

@Component({
  selector: 'phms-hearing-enrollment-setup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    UiButtonDirective,
    UiControlDirective,
  ],
  template: `<section aria-label="Hearing enrollment setup" class="setup">
    <h3>
      {{ templateOnly ? 'Hearing lifecycle template' : 'Enrollment setup' }}
    </h3>
    <p>
      {{
        templateOnly
          ? 'Create an editable hearing lifecycle with eligibility first, linked hearing ownership and required numbering.'
          : 'Connect an installed source to a published hearing workflow. New runs retain the selected configuration and publication.'
      }}
    </p>
    <button
      uiButton
      type="button"
      (click)="load()"
      [disabled]="
        busy || interactionBlocked || !(templateOnly ? canCreate() : canRead())
      "
    >
      {{ catalog ? 'Refresh setup catalog' : 'Load hearing setup' }}
    </button>
    <p *ngIf="error" role="alert">{{ error }}</p>
    <p *ngIf="notice" role="status">{{ notice }}</p>
    <p *ngIf="busy" role="status">Updating hearing setup…</p>
    <ng-container *ngIf="catalog as data">
      <fieldset
        *ngIf="templateOnly"
        [disabled]="busy || interactionBlocked || !canCreate()"
      >
        <legend>Initial hearing lifecycle</legend>
        <label
          >Workflow name<input
            uiControl
            [(ngModel)]="name"
            (ngModelChange)="changed()"
            maxlength="160"
        /></label>
        <label *ngIf="data.configurations.length > 1"
          >Hearing policy settings<select
            uiControl
            [(ngModel)]="configurationId"
            (ngModelChange)="changed()"
          >
            <option [ngValue]="null">Choose policy settings</option>
            <option *ngFor="let c of data.configurations" [ngValue]="c.id">
              {{ c.label }}
            </option>
          </select></label
        >
        <p *ngIf="!data.configurations.length">
          Hearing policy settings are not installed. Ask a deployment
          administrator to run environment bootstrap.
        </p>
        <button
          uiButton
          type="button"
          (click)="create()"
          [disabled]="!name.trim() || !data.configurations.length"
        >
          Create hearing lifecycle draft
        </button>
      </fieldset>
      <ng-container *ngIf="!templateOnly">
        <p *ngIf="active">Active connection: {{ activeLabel }}</p>
        <fieldset [disabled]="busy || interactionBlocked || !canAuthor()">
          <legend>Enrollment connection</legend>
          <label
            >Population source<select
              uiControl
              [(ngModel)]="sourceCode"
              (ngModelChange)="changed()"
            >
              <option *ngFor="let s of data.sources" [value]="s.code">
                {{ s.label }} · {{ s.installed ? 'Installed' : 'Unavailable' }}
              </option>
            </select></label
          >
          <p *ngIf="source">
            Scope: {{ source.scopeKey }} · Hearing type:
            {{ source.hearingTypeCode }}
          </p>
          <label
            >Published hearing workflow<select
              uiControl
              [(ngModel)]="publicationId"
              (ngModelChange)="changed()"
            >
              <option [ngValue]="null">Choose published workflow</option>
              <option *ngFor="let p of publications" [ngValue]="p.id">
                {{ p.displayName }} · {{ p.definitionVersionCode }}
              </option>
            </select></label
          >
          <label
            >Setup reason<textarea
              uiControl
              [(ngModel)]="reason"
              (ngModelChange)="changed()"
              maxlength="1000"
            ></textarea>
          </label>
          <button
            uiButton
            type="button"
            (click)="save()"
            [disabled]="!publicationId || !reason.trim() || !source"
          >
            Save enrollment draft
          </button>
        </fieldset>
        <p *ngIf="saved">
          Saved configuration {{ saved.id }} · {{ saved.status }} · entry
          {{ saved.entryStepCode }}
        </p>
        <button
          *ngIf="saved"
          uiButton
          type="button"
          (click)="check()"
          [disabled]="busy || interactionBlocked || dirty"
        >
          Check readiness
        </button>
        <p *ngIf="dirty">
          Save your changes before checking readiness or activating.
        </p>
        <section *ngIf="report as r" aria-label="Enrollment readiness">
          <p>
            Publication: {{ r.publicationReady ? 'Ready' : 'Needs setup' }} ·
            Enrollment connection:
            {{
              saved?.status === 'active'
                ? 'Active'
                : r.connectionReady
                  ? 'Ready to activate'
                  : 'Needs setup'
            }}
            · Manual execution:
            {{ r.manualReady ? 'Ready' : 'Needs schedule setup' }} · Recurring
            execution: {{ r.recurringReady ? 'Ready' : 'Off or needs setup' }}
          </p>
          <ul>
            <li *ngFor="let c of r.checks">
              <strong>{{ c.ready ? 'Ready' : 'Needs setup' }}</strong> ·
              {{ c.label }}
              <ng-container *ngIf="!c.ready"
                >{{ c.message }} {{ c.owner }}
                <a *ngIf="c.actionUrl" [routerLink]="actionLink(c.actionUrl)">Open setup</a></ng-container
              >
            </li>
          </ul>
        </section>
        <button
          *ngIf="saved?.status === 'draft'"
          uiButton
          type="button"
          (click)="activate()"
          [disabled]="
            busy ||
            interactionBlocked ||
            dirty ||
            !report?.connectionReady ||
            !canActivate()
          "
        >
          Activate enrollment connection
        </button>
        <p>
          Use the published schedule below to run now or enable recurring
          starts. Changing this connection requires a new schedule version;
          captured work keeps its original settings.
        </p>
        <a routerLink="/platform-admin/operations"
          >Open schedules and run history</a
        >
      </ng-container>
    </ng-container>
  </section>`,
  styles: [
    `
      .setup {
        display: block;
        padding: 1rem;
        border: 1px solid var(--custom-border-color);
        border-radius: 0.35rem;
        margin-block: 1rem;
        overflow-wrap: anywhere;
        min-width: 0;
      }
      fieldset {
        border: 0;
        padding: 0.5rem 0;
        display: grid;
        gap: 0.75rem;
        max-width: 52rem;
      }
      label {
        display: grid;
        gap: 0.3rem;
      }
      input,
      select,
      textarea {
        width: 100%;
        min-width: 0;
      }
      button {
        margin: 0.4rem 0.4rem 0.4rem 0;
      }
      li {
        margin-block: 0.5rem;
      }
    `,
  ],
})
export class HearingEnrollmentSetupComponent implements OnDestroy {
  @Input() workflowCode = '';
  @Input() templateOnly = false;
  @Input() interactionBlocked = false;
  private readonly http = inject(HttpClient);
  private readonly apis = inject(AppApis);
  private readonly router = inject(Router);
  actionLink(url: string) { return this.router.parseUrl(url); }
  private readonly destroy = inject(DestroyRef);
  private readonly roles = inject(RoleService);
  private readonly state = inject(HearingEnrollmentStateService);
  readonly canRead = this.roles.hasAnyPermissionSignal([
    'platform_access_admin.read',
  ]);
  readonly canCreate = this.roles.hasAnyPermissionSignal([
    'workflow_definition.author',
  ]);
  readonly canAuthor = this.roles.hasAnyPermissionSignal([
    'platform_access_admin.author',
  ]);
  readonly canActivate = this.roles.hasAnyPermissionSignal([
    'platform_access_admin.publish',
  ]);
  catalog: Catalog | null = null;
  saved: Setup | null = null;
  active: Setup | null = null;
  report: Report | null = null;
  busy = false;
  dirty = false;
  error = '';
  notice = '';
  name = '';
  reason = '';
  configurationId: number | null = null;
  sourceCode = '';
  publicationId: number | null = null;
  private intent: string | null = null;
  private get url() {
    return this.apis.core.manual.getHearingEnrollmentSetup();
  }
  get source() {
    return this.catalog?.sources.find((s) => s.code === this.sourceCode);
  }
  get publications() {
    return (
      this.catalog?.publications.filter(
        (p) =>
          (!this.workflowCode || p.workflowCode === this.workflowCode) &&
          p.scopeTypeCode === this.source?.scopeTypeCode &&
          p.scopeKey === this.source?.scopeKey,
      ) ?? []
    );
  }
  get activeLabel() {
    const publication = this.catalog?.publications.find(
      (p) => p.id === this.active?.definition.definitionVersionId,
    );
    return publication
      ? `${publication.displayName} · ${publication.definitionVersionCode}`
      : 'Published hearing workflow';
  }
  changed() {
    this.dirty = true;
    this.state.dirty.set(true);
    this.report = null;
    this.intent = null;
  }
  ngOnDestroy() {
    this.state.dirty.set(false);
    this.state.busy.set(false);
  }
  @HostListener('window:beforeunload', ['$event']) beforeUnload(
    event: BeforeUnloadEvent,
  ) {
    if (this.dirty || this.busy) {
      event.preventDefault();
      event.returnValue = '';
    }
  }
  private options(eTag?: string, key?: string) {
    let headers = new HttpHeaders({
      'skip-error-interceptor': 'yes',
      'skip-loading-interceptor': 'yes',
    });
    if (eTag) headers = headers.set('If-Match', eTag);
    if (key) headers = headers.set('Idempotency-Key', key);
    return { withCredentials: true, headers };
  }
  private async perform(action: () => Promise<void>) {
    if (this.busy || this.interactionBlocked) return;
    this.busy = true;
    this.state.busy.set(true);
    this.error = '';
    this.notice = '';
    try {
      await action();
    } catch (e: any) {
      if ([401, 403].includes(e.status)) {
        this.catalog = null;
        this.saved = null;
        this.active = null;
        this.report = null;
      }
      this.error =
        e.status === 403
          ? 'Current permissions do not allow this setup action.'
          : (e.error?.detail ??
            'Setup could not complete. Your choices are kept; refresh before retrying a stale operation.');
    } finally {
      this.busy = false;
      this.state.busy.set(false);
      this.state.dirty.set(this.dirty);
    }
  }
  async load() {
    if (!(this.templateOnly ? this.canCreate() : this.canRead())) return;
    await this.perform(async () => {
      const data = await firstValueFrom(
        this.http
          .get<Catalog>(
            this.templateOnly ? this.url + '/templates' : this.url,
            this.options(),
          )
          .pipe(takeUntilDestroyed(this.destroy)),
      );
      this.catalog = data;
      this.active = data.versions.find((v) => v.status === 'active') ?? null;
      if (!this.dirty) {
        this.saved =
          data.versions.find(
            (v) =>
              v.status === 'draft' &&
              (!this.workflowCode || v.workflowCode === this.workflowCode),
          ) ??
          (this.active &&
          (!this.workflowCode || this.active.workflowCode === this.workflowCode)
            ? this.active
            : null);
        this.sourceCode =
          this.saved?.definition.sourceCode ?? data.sources[0]?.code ?? '';
        this.publicationId =
          this.saved &&
          (!this.workflowCode || this.saved.workflowCode === this.workflowCode)
            ? this.saved.definition.definitionVersionId
            : this.publications.length === 1
              ? this.publications[0].id
              : null;
      }
    });
  }
  async create() {
    if (!this.canCreate()) return;
    await this.perform(async () => {
      this.intent ??= crypto.randomUUID();
      const key = this.intent;
      const draft = await firstValueFrom(
        this.http
          .post<{
            id: number;
          }>(
            this.url + '/template',
            {
              requestId: key,
              displayName: this.name.trim(),
              configurationVersionId: this.configurationId,
            },
            this.options(undefined, key),
          )
          .pipe(takeUntilDestroyed(this.destroy)),
      );
      this.dirty = false;
      this.state.dirty.set(false);
      this.state.busy.set(false);
      this.intent = null;
      await this.router.navigate(
        ['/platform-admin/workflow-registry-diagnostics'],
        { queryParams: { registryView: 'drafts', draft: draft.id } },
      );
    });
  }
  async save() {
    if (!this.canAuthor() || !this.source || !this.publicationId) return;
    await this.perform(async () => {
      this.intent ??= crypto.randomUUID();
      const draft = this.catalog?.versions.find((v) => v.status === 'draft');
      this.saved = await firstValueFrom(
        this.http
          .post<Setup>(
            this.url + '/draft',
            {
              expectedRevision: draft?.revision ?? 0,
              definition: {
                definitionVersionId: this.publicationId,
                sourceCode: this.sourceCode,
                scopeTypeCode: this.source!.scopeTypeCode,
                scopeKey: this.source!.scopeKey,
                hearingTypeCode: this.source!.hearingTypeCode,
              },
              reason: this.reason,
            },
            this.options(draft?.eTag, this.intent),
          )
          .pipe(takeUntilDestroyed(this.destroy)),
      );
      this.catalog!.versions = [
        this.saved,
        ...this.catalog!.versions.filter((v) => v.id !== this.saved!.id),
      ];
      this.dirty = false;
      this.intent = null;
      this.notice = 'Enrollment draft saved.';
    });
  }
  async check() {
    if (!this.saved || this.dirty) return;
    await this.perform(async () => {
      this.report = await firstValueFrom(
        this.http
          .get<Report>(
            `${this.url}/${this.saved!.id}/readiness`,
            this.options(),
          )
          .pipe(takeUntilDestroyed(this.destroy)),
      );
    });
  }
  async activate() {
    if (
      !this.saved ||
      this.dirty ||
      !this.report?.connectionReady ||
      !this.canActivate()
    )
      return;
    await this.perform(async () => {
      this.intent ??= crypto.randomUUID();
      this.saved = await firstValueFrom(
        this.http
          .post<Setup>(
            `${this.url}/${this.saved!.id}/activate`,
            { expectedRevision: this.saved!.revision, reason: this.reason },
            this.options(this.saved!.eTag, this.intent),
          )
          .pipe(takeUntilDestroyed(this.destroy)),
      );
      this.active = this.saved;
      this.intent = null;
      this.notice =
        'Enrollment connection activated. Publish a schedule version to run it.';
    });
  }
}
